import React from 'react';
import { motion } from 'framer-motion';

function WealthEstatePlanning() {
  const streams = [
    { title: 'Trusts & Gifting', points: ['Revocable/irrevocable trusts', 'Lifetime gifting strategies', 'Charitable vehicles'] },
    { title: 'Family Governance', points: ['Succession frameworks', 'Education for heirs', 'Family charter design'] },
    { title: 'Legacy & Philanthropy', points: ['Donor‑advised funds', 'Foundation setup', 'Impact objectives'] },
    { title: 'Advisor Coordination', points: ['Legal & tax collaboration', 'Entity structuring', 'Comprehensive documentation'] },
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="relative h-[55vh] overflow-hidden">
        <img src="/images/real-people/portrait-businessman-smile-meeting-strategy-teamwork-planning-project-office-diversity-collaboration-brainstorming-company-with-pride-documents-conference-room_590464-372491.jpg" alt="Wealth and estate planning" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/80" />
        <div className="relative z-10 flex h-full items-center justify-center text-center px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="text-white text-5xl font-light mb-4">Wealth & <span className="font-semibold text-[#fbbf24]">Estate Planning</span></h1>
            <p className="text-white/90 text-xl max-w-3xl">Preserve and transfer your legacy with a coordinated, long‑term plan.</p>
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div data-aos="fade-up" className="text-center mb-14">
            <h2 className="text-3xl font-semibold text-gray-900">A Cohesive Legacy Strategy</h2>
            <p className="text-gray-600 text-lg mt-2">We collaborate with your legal and tax advisors to align structures, beneficiaries and intentions.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {streams.map((s, i) => (
              <div key={s.title} data-aos="fade-up" data-aos-delay={`${150 + i * 100}`} className="p-8 bg-white rounded-2xl shadow-lg border border-gray-100">
                <div className="w-12 h-12 rounded-full bg-[#0056d7] text-white font-semibold flex items-center justify-center mb-5">{String(i+1).padStart(2,'0')}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{s.title}</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  {s.points.map(pt => (<li key={pt} className="flex items-start gap-2"><span className="mt-1 w-2 h-2 rounded-full bg-[#0056d7]" />{pt}</li>))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-14 text-center">
            <motion.a href="/contact" className="inline-block px-10 py-4 rounded-full bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-black font-medium shadow-lg hover:shadow-amber-500/30 transition-colors" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.96 }}>
              Discuss Your Legacy Plan
            </motion.a>
          </div>
        </div>
      </section>

      {/* Coordination Workflow */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-6">
          <div data-aos="fade-up" className="text-center mb-14">
            <h2 className="text-3xl font-semibold text-gray-900">How We Work Together</h2>
            <p className="text-gray-600 text-lg mt-2">A structured, collaborative process across your advisory team.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[{t:'Discovery',d:'Values, beneficiaries, assets, entities, and priorities.'},{t:'Design',d:'Draft structures with counsel and model implications.'},{t:'Implement',d:'Finalize, fund and document entities.'},{t:'Evolve',d:'Annual reviews; adapt as family & laws change.'}].map((s,i)=>(
              <div key={s.t} data-aos="fade-up" data-aos-delay={`${150+i*100}`} className="p-8 bg-white rounded-2xl shadow-lg border border-gray-100">
                <div className="w-12 h-12 rounded-full bg-[#0056d7] text-white font-semibold flex items-center justify-center mb-5">{String(i+1).padStart(2,'0')}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{s.t}</h3>
                <p className="text-gray-600">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div data-aos="fade-up" className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-gray-900">Estate Planning FAQs</h2>
          </div>
          <div className="space-y-6">
            {[
              {q:'Do you draft legal documents?', a:'We do not practice law; we coordinate closely with your attorney to design and implement the plan.'},
              {q:'Can you help with a family meeting?', a:'Yes, we facilitate sessions to align goals, education and governance structures.'},
              {q:'What if my situation is complex?', a:'Our process scales to multi‑jurisdictional families, operating companies and charitable entities.'}
            ].map((f,i)=> (
              <div key={f.q} data-aos="fade-up" data-aos-delay={`${100+i*100}`} className="p-6 rounded-xl bg-gray-50 border border-gray-100">
                <div className="font-semibold text-gray-900 mb-1">{f.q}</div>
                <div className="text-gray-700">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default WealthEstatePlanning;


